import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import "./SMED1.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

function SMED1(props) {
  const { x1200PxLogo_Icam__20081 } = props;
  const [hasImplemented, setHasImplemented] = useState(false);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState({
    // Part 1: Current state - SMED implementation today
    // Changeover time diagnosis
    timesMeasured: "",
    averageChangeoverTime: "",
    equipmentTypes: "",
    operationsDistinction: "",
    wasteIdentification: "",
    
    // Methodology and structuring
    structuredApproach: "",
    videoAnalysis: "",
    operatorsInvolvement: "",
    standardizedProcedures: "",
    concreteGains: "",
    
    // Part 2: Desired future state - Targets & SMED vision
    // Ambitions and expected gains
    targetChangeoverTime: "",
    smedExpansion: "",
    productionFlexibility: "",
    leanIntegration: "",
    automationPlans: "",
    
    // Part 3: Monitoring, control & gaps
    // Performance measurement
    smedIndicators: "",
    changeoverFrequency: "",
    progressIntegration: "",
    deviationsTracking: "",
    trainingPlan: "",
    
    // Organization and improvement dynamics
    teamLeadersRole: "",
    bestPracticesDocumentation: "",
    resistanceManagement: "",
    improvementPlan: "",
    roadmap: ""
  });
  
  const [uploadStatus, setUploadStatus] = useState({
    isUploading: false,
    progress: 0,
    error: null,
    success: false,
    fileName: ""
  });
  const fileInputRef = useRef(null);
  const questionnaireRef = useRef(null);

  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // File type validation
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/svg+xml', 
                         'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                         'application/vnd.ms-excel',
                         'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                         'application/msword'];
    if (!allowedTypes.includes(file.type)) {
      setUploadStatus({
        ...uploadStatus,
        error: "Unsupported file type. Please use PDF, JPG, PNG, SVG, Excel or Word.",
        success: false
      });
      return;
    }

    // Size validation (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadStatus({
        ...uploadStatus,
        error: "The file is too large. Maximum size: 10MB",
        success: false
      });
      return;
    }

    try {
      setUploadStatus({
        isUploading: true,
        progress: 0,
        error: null,
        success: false,
        fileName: file.name
      });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('answers', JSON.stringify(answers));

      const xhr = new XMLHttpRequest();
      
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadStatus(prev => ({
            ...prev,
            progress
          }));
        }
      };

      const uploadPromise = new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve(JSON.parse(xhr.response));
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
      });

      xhr.open('POST', '/api/smed/upload');
      xhr.send(formData);

      const response = await uploadPromise;

      setUploadStatus({
        isUploading: false,
        progress: 100,
        error: null,
        success: true,
        fileName: file.name,
        fileId: response.file_id
      });

    } catch (error) {
      setUploadStatus({
        isUploading: false,
        progress: 0,
        error: "An error occurred during upload. Please try again.",
        success: false,
        fileName: ""
      });
    }
  };

  const handleAnswerChange = (field, value) => {
    setAnswers({
      ...answers,
      [field]: value
    });
  };

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
      if (questionnaireRef.current) {
        questionnaireRef.current.scrollTop = 0;
      }
    } else {
      setShowImport(true);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      if (questionnaireRef.current) {
        questionnaireRef.current.scrollTop = 0;
      }
    }
  };

  const handleYesClick = () => {
    setHasImplemented(true);
    setShowQuestionnaire(true);
  };

  // Progress indicator
  const renderProgressIndicator = () => {
    return (
      <div className="progress-indicator">
        <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>1</div>
        <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>2</div>
        <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>3</div>
      </div>
    );
  };

  // Render questions
  const renderQuestionnaire = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="questionnaire-step">
            <h2 className="step-title">Part 1: Current state - SMED implementation today</h2>
            
            <div className="question-group">
              <h3 className="question-group-title">Changeover Time Diagnosis</h3>
              
              <div className="question-item">
                <label htmlFor="timesMeasured">Have you precisely measured your changeover times (before/after SMED)?</label>
                <textarea 
                  id="timesMeasured" 
                  value={answers.timesMeasured} 
                  onChange={(e) => handleAnswerChange('timesMeasured', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="averageChangeoverTime">What is the current average changeover time on your critical machines or workstations?</label>
                <textarea 
                  id="averageChangeoverTime" 
                  value={answers.averageChangeoverTime} 
                  onChange={(e) => handleAnswerChange('averageChangeoverTime', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="equipmentTypes">Which types of equipment are involved in the SMED implementation?</label>
                <textarea 
                  id="equipmentTypes" 
                  value={answers.equipmentTypes} 
                  onChange={(e) => handleAnswerChange('equipmentTypes', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="operationsDistinction">Do you clearly distinguish external operations (done while machine runs) from internal operations (done only when stopped)?</label>
                <textarea 
                  id="operationsDistinction" 
                  value={answers.operationsDistinction} 
                  onChange={(e) => handleAnswerChange('operationsDistinction', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="wasteIdentification">Have you identified wastes and downtime related to current changeovers?</label>
                <textarea 
                  id="wasteIdentification" 
                  value={answers.wasteIdentification} 
                  onChange={(e) => handleAnswerChange('wasteIdentification', e.target.value)}
                  rows="3"
                />
              </div>
            </div>

            <div className="question-group">
              <h3 className="question-group-title">Methodology & Structuring</h3>
              
              <div className="question-item">
                <label htmlFor="structuredApproach">Have you implemented a structured SMED approach (steps, tools, indicators)?</label>
                <textarea 
                  id="structuredApproach" 
                  value={answers.structuredApproach} 
                  onChange={(e) => handleAnswerChange('structuredApproach', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="videoAnalysis">Do you use videos or field observations to analyze changeovers?</label>
                <textarea 
                  id="videoAnalysis" 
                  value={answers.videoAnalysis} 
                  onChange={(e) => handleAnswerChange('videoAnalysis', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="operatorsInvolvement">Are operators involved in the analysis and reorganization of changeovers?</label>
                <textarea 
                  id="operatorsInvolvement" 
                  value={answers.operatorsInvolvement} 
                  onChange={(e) => handleAnswerChange('operatorsInvolvement', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="standardizedProcedures">Have you standardized format/tool change procedures?</label>
                <textarea 
                  id="standardizedProcedures" 
                  value={answers.standardizedProcedures} 
                  onChange={(e) => handleAnswerChange('standardizedProcedures', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="concreteGains">What concrete gains have already been obtained (time reduction, quality, flexibility)?</label>
                <textarea 
                  id="concreteGains" 
                  value={answers.concreteGains} 
                  onChange={(e) => handleAnswerChange('concreteGains', e.target.value)}
                  rows="3"
                />
              </div>
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="questionnaire-step">
            <h2 className="step-title">Part 2: Desired state - SMED goals & future vision</h2>

            <div className="question-group">
              <h3 className="question-group-title">Ambitions & Expected Gains</h3>

              <div className="question-item">
                <label htmlFor="targetChangeoverTime">What is your target changeover time per machine or key process?</label>
                <textarea
                  id="targetChangeoverTime"
                  value={answers.targetChangeoverTime}
                  onChange={(e) =>
                    handleAnswerChange("targetChangeoverTime", e.target.value)
                  }
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="smedExpansion">
                  Do you plan to extend the SMED approach to other workstations or production lines?
                </label>
                <textarea
                  id="smedExpansion"
                  value={answers.smedExpansion}
                  onChange={(e) =>
                    handleAnswerChange("smedExpansion", e.target.value)
                  }
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="productionFlexibility">
                  Are you aiming for a more flexible production system (more changeovers, smaller batches)?
                </label>
                <textarea
                  id="productionFlexibility"
                  value={answers.productionFlexibility}
                  onChange={(e) =>
                    handleAnswerChange("productionFlexibility", e.target.value)
                  }
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="leanIntegration">
                  Would you like to combine SMED with other Lean tools (TPM, 5S, Kanbanâ€¦)?
                </label>
                <textarea
                  id="leanIntegration"
                  value={answers.leanIntegration}
                  onChange={(e) =>
                    handleAnswerChange("leanIntegration", e.target.value)
                  }
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="automationPlans">
                  Do you plan to automate certain changeover steps (automatic adjustments, quick-change tools)?
                </label>
                <textarea
                  id="automationPlans"
                  value={answers.automationPlans}
                  onChange={(e) =>
                    handleAnswerChange("automationPlans", e.target.value)
                  }
                  rows="3"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="questionnaire-step">
            <h2 className="step-title">Part 3: Monitoring, Control & Gap Analysis</h2>

            <div className="question-group">
              <h3 className="question-group-title">Performance Measurement</h3>

              <div className="question-item">
                <label htmlFor="smedIndicators">
                  Do you have clear SMED indicators monitored regularly?
                </label>
                <textarea
                  id="smedIndicators"
                  value={answers.smedIndicators}
                  onChange={(e) =>
                    handleAnswerChange("smedIndicators", e.target.value)
                  }
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="changeoverFrequency">
                  Do you measure the frequency of changeovers and their impact on overall productivity?
                </label>
                <textarea
                  id="changeoverFrequency"
                  value={answers.changeoverFrequency}
                  onChange={(e) =>
                    handleAnswerChange(
                      "changeoverFrequency",
                      e.target.value
                    )
                  }
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="progressIntegration">
                  Are SMED improvements integrated into your management rituals (performance reviews, visual boards, QRQC)?
                </label>
                <textarea
                  id="progressIntegration"
                  value={answers.progressIntegration}
                  onChange={(e) =>
                    handleAnswerChange("progressIntegration", e.target.value)
                  }
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="deviationsTracking">
                  How do you track deviations or regressions after optimization?
                </label>
                <textarea
                  id="deviationsTracking"
                  value={answers.deviationsTracking}
                  onChange={(e) =>
                    handleAnswerChange("deviationsTracking", e.target.value)
                  }
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="trainingPlan">
                  Do you have a training and upskilling plan for SMED?
                </label>
                <textarea
                  id="trainingPlan"
                  value={answers.trainingPlan}
                  onChange={(e) =>
                    handleAnswerChange("trainingPlan", e.target.value)
                  }
                  rows="3"
                />
              </div>
            </div>

            <div className="question-group">
              <h3 className="question-group-title">
                Organization & Improvement Dynamics
              </h3>

              <div className="question-item">
                <label htmlFor="teamLeadersRole">
                  What is the role of team leaders or methods technicians in SMED deployment?
                </label>
                <textarea
                  id="teamLeadersRole"
                  value={answers.teamLeadersRole}
                  onChange={(e) =>
                    handleAnswerChange("teamLeadersRole", e.target.value)
                  }
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="bestPracticesDocumentation">
                  Do you have a structured approach to documenting and capitalizing on best practices?
                </label>
                <textarea
                  id="bestPracticesDocumentation"
                  value={answers.bestPracticesDocumentation}
                  onChange={(e) =>
                    handleAnswerChange(
                      "bestPracticesDocumentation",
                      e.target.value
                    )
                  }
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="resistanceManagement">
                  How do you manage resistance to change when reducing changeover times?
                </label>
                <textarea
                  id="resistanceManagement"
                  value={answers.resistanceManagement}
                  onChange={(e) =>
                    handleAnswerChange("resistanceManagement", e.target.value)
                  }
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="improvementPlan">
                  Have you implemented an improvement plan related to SMED?
                </label>
                <textarea
                  id="improvementPlan"
                  value={answers.improvementPlan}
                  onChange={(e) =>
                    handleAnswerChange("improvementPlan", e.target.value)
                  }
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="roadmap">
                  What is your roadmap to reach an advanced SMED maturity level in the coming months?
                </label>
                <textarea
                  id="roadmap"
                  value={answers.roadmap}
                  onChange={(e) =>
                    handleAnswerChange("roadmap", e.target.value)
                  }
                  rows="3"
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const element = document.querySelector(".smed-page");
      if (!element) {
        alert("âŒ Element not found!");
        return;
      }

      // Remove gradients and CSS vars
      document.querySelectorAll("*").forEach((el) => {
        const bgImage = window.getComputedStyle(el).backgroundImage;
        if (bgImage && bgImage.includes("gradient")) {
          el.style.backgroundImage = "none";
        }

        const bgColor = window.getComputedStyle(el).backgroundColor;
        if (
          bgColor.includes("var(") ||
          bgColor === "transparent" ||
          bgColor === "rgba(0, 0, 0, 0)"
        ) {
          el.style.backgroundColor = "#ffffff";
        }
      });

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      const date = new Date().toISOString().slice(0, 10);
      pdf.save(`SMED_${date}.pdf`);
      alert("âœ… PDF generated successfully!");
    } catch (err) {
      console.error("PDF error:", err);
      alert("âŒ PDF error: " + (err.message || "unknown"));
    }
  };

  const saveLeanAnswers = async (moduleName) => {
    try {
      const caseId = localStorage.getItem("caseId");
      if (!caseId) {
        alert("No active case found!");
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/lean/${moduleName}/save`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            case_id: caseId,
            answers: answers,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            `Save error (${response.status}): ${data.message}`
        );
      }

      alert(`âœ… ${moduleName.toUpperCase()} answers saved successfully!`);
      handleNextStep();
    } catch (error) {
      alert("" + error.message);
    }
  };

  return (
    <div className="container-center-horizontal">
      <div className="smed-page screen">
        <div className="header">
          <Link to="/home">
            <img
              className="logo-icam"
              src="/img/1200px-logo-icam---2008-1@2x.png"
              alt="ICAM Logo"
            />
          </Link>
        </div>

        <div className="questionnaire-container">
          <h1 className="question-title">
            SMED Methodology - Reducing Changeover Times
          </h1>

          {!showQuestionnaire ? (
            <div className="question-section">
              <p className="question-text">
                Has your company already implemented the SMED methodology?
              </p>

              <div className="buttons-container">
                <button
                  className={`choice-button ${hasImplemented ? "active" : ""}`}
                  onClick={handleYesClick}
                >
                  Yes
                </button>
                <button
                  className={`choice-button ${
                    !hasImplemented ? "active" : ""
                  }`}
                  onClick={() => setHasImplemented(false)}
                >
                  No
                </button>
              </div>

              {!hasImplemented && (
                <div className="redirect-section">
                  <p className="redirect-text">
                    We recommend starting by understanding the basics of Lean:
                  </p>
                  <Link to="/Kanban-1" className="redirect-button">
                    Go to the Kanban method
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <>
              {!showImport ? (
                <div
                  className="questionnaire-container-inner"
                  ref={questionnaireRef}
                >
                  {renderProgressIndicator()}
                  {renderQuestionnaire()}

                  <div className="navigation-buttons">
                    {currentStep > 1 && (
                      <button
                        className="nav-button prev-button"
                        onClick={handlePrevStep}
                      >
                        Previous
                      </button>
                    )}

                    <button
                      className="nav-button next-button"
                      onClick={() => saveLeanAnswers("smed")}
                    >
                      {currentStep < 3 ? "Next" : "Finish"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="file-upload-section">
                  <h2 className="upload-title">
                    Import your existing documents
                  </h2>
                  <p className="upload-description">
                    You can now import your SMED documents, timing sheets or
                    existing procedures:
                  </p>

                  <div className="upload-container">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden-file-input"
                      accept=".pdf,.jpg,.jpeg,.png,.svg,.xlsx,.xls,.doc,.docx"
                      onChange={handleFileUpload}
                      disabled={uploadStatus.isUploading}
                    />

                    <button
                      className="import-button"
                      onClick={handleImportClick}
                      disabled={uploadStatus.isUploading}
                    >
                      {uploadStatus.isUploading
                        ? "Uploadingâ€¦"
                        : "Import a file"}
                    </button>

                    {uploadStatus.isUploading && (
                      <div className="upload-progress">
                        <div className="progress-bar">
                          <div
                            className="progress-bar-fill"
                            style={{ width: `${uploadStatus.progress}%` }}
                          ></div>
                        </div>
                        <p className="progress-text">
                          Upload in progress... {uploadStatus.progress}%
                        </p>
                      </div>
                    )}

                    {uploadStatus.error && (
                      <div className="upload-error">
                        <p>{uploadStatus.error}</p>
                      </div>
                    )}

                    {uploadStatus.success && (
                      <div className="upload-success">
                        <p>
                          File {uploadStatus.fileName} uploaded successfully!
                        </p>
                        {uploadStatus.fileId && (
                          <button
                            className="download-button"
                            onClick={() =>
                              window.open(
                                `/api/smed/${uploadStatus.fileId}`,
                                "_blank"
                              )
                            }
                          >
                            View file
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="navigation-buttons">
                    <button
                      className="nav-button prev-button"
                      onClick={() => setShowImport(false)}
                    >
                      Back to questionnaire
                    </button>

                    <Link to="/smed-2" className="nav-button next-button">
                      Continue
                    </Link>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <button
          className="download-button"
          onClick={handleDownloadPDF}
          disabled={uploadStatus.isUploading}
        >
          {uploadStatus.isUploading
            ? "Generating PDF..."
            : "Download as PDF"}
        </button>
      </div>
    </div>
  );
}

export default SMED1;
